import { useState, type ReactNode } from 'react';
import { ZodError } from 'zod';
import { db } from '../../lib/firebase';
import { saveDoc, swapOrder, type CollName } from '../../lib/firestore-write';
import { ReorderButtons, toast } from '../ui';

// ---------- error helpers ----------

export function errOf(e: unknown): string {
  if (e instanceof ZodError) {
    const first = e.issues[0];
    return first ? first.message : 'Validation failed';
  }
  if (e instanceof Error) return e.message;
  return String(e);
}

/** Map zod issues to their first path segment so editors can show inline hints. */
export function zodFields(e: unknown): Record<string, string> {
  if (!(e instanceof ZodError)) return {};
  const m: Record<string, string> = {};
  for (const issue of e.issues) {
    const key = String(issue.path[0] ?? '_');
    if (!(key in m)) m[key] = issue.message;
  }
  return m;
}

/** Validate-then-write hook shared by all admin editors. */
export function useSaver(
  coll: CollName,
  id: string,
  build: () => Record<string, unknown>,
  onDone: () => void
) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    setErrors({});
    try {
      await saveDoc(db, coll, id, build());
      toast('Saved', 'ok');
      onDone();
    } catch (e) {
      const f = zodFields(e);
      setErrors(f);
      const msg = Object.values(f)[0] ?? errOf(e);
      toast(msg, 'err');
    } finally {
      setSaving(false);
    }
  };
  return { errors, saving, save, setErrors };
}

// ---------- presentational bits ----------

/** Toolbar row for admin tabs: count label + optional live search + action slot. */
export function AdminToolbar({
  label,
  count,
  query,
  onQuery,
  children
}: {
  label: string;
  count?: ReactNode;
  query?: string;
  onQuery?: (v: string) => void;
  children?: ReactNode;
}) {
  return (
    <div className="admin-bar">
      <div className="admin-bar-left">
        <span className="mono" style={{ fontSize: 11, letterSpacing: '.1em', color: 'var(--ink3)' }}>
          {label}
          {count !== undefined ? ` · ${count}` : ''}
        </span>
      </div>
      <div className="admin-bar-right">
        {onQuery && (
          <div className="admin-search">
            <span aria-hidden>⌕</span>
            <input
              type="search"
              placeholder="Filter…"
              value={query ?? ''}
              onChange={e => onQuery(e.target.value)}
              aria-label="Filter items"
            />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function ItemRow({
  hidden,
  badge,
  badgeTone = 'line',
  title,
  sub,
  mono,
  children
}: {
  hidden?: boolean;
  badge?: string;
  badgeTone?: 'ok' | 'warn' | 'line';
  title: ReactNode;
  sub?: ReactNode;
  mono?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={`admin-item${hidden ? ' hidden-item' : ''}`}>
      {mono ? <span className="mono" style={{ fontSize: 11, color: 'var(--ink3)' }}>{mono}</span> : null}
      {badge ? <span className={`pill ${badgeTone}`}>{badge}</span> : null}
      {hidden ? <span className="pill warn">Hidden</span> : null}
      <span className="t">
        {title}
        {sub ? <small>{sub}</small> : null}
      </span>
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>{children}</span>
    </div>
  );
}

export function HiddenBtn({ hidden, onClick }: { hidden: boolean; onClick: () => void }) {
  return (
    <button className={`row-btn${hidden ? ' on' : ''}`} onClick={onClick} title={hidden ? 'Make visible' : 'Hide from site'}>
      {hidden ? 'Show' : 'Hide'}
    </button>
  );
}

export function EditBtn({ onClick }: { onClick: () => void }) {
  return (
    <button className="row-btn" onClick={onClick} title="Edit">
      Edit
    </button>
  );
}

export function DeleteBtn({ onClick }: { onClick: () => void }) {
  return (
    <button className="row-btn danger" onClick={onClick} title="Delete">
      Delete
    </button>
  );
}

export function NewBtn({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button className="btn btn-primary btn-sm" onClick={onClick}>
      {children}
    </button>
  );
}

/**
 * Reorder controls that swap `id` with its neighbour *among visible rows*.
 * Hidden rows are skipped so the public order stays predictable.
 */
export function SwapBar<T extends { id: string; hidden?: boolean }>({
  coll,
  list,
  id,
  hidden,
  label
}: {
  coll: CollName;
  list: T[];
  id: string;
  hidden?: boolean;
  label: string
}) {
  const eff = hidden ? list : list.filter(x => !x.hidden);
  const i = eff.findIndex(x => x.id === id);
  const move = (dir: 'up' | 'down') => {
    const j = dir === 'up' ? i - 1 : i + 1;
    if (i < 0 || j < 0 || j >= eff.length) return;
    void swapOrder(db, coll, eff[i].id, eff[j].id).catch(e => toast(errOf(e), 'err'));
  };
  return (
    <ReorderButtons
      label={label}
      canUp={i > 0}
      canDown={i >= 0 && i < eff.length - 1}
      onUp={() => move('up')}
      onDown={() => move('down')}
    />
  );
}

/** Small on/off control used inside modals and the settings form. */
export function SwitchRow({
  label,
  on,
  onToggle,
  hint
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
  hint?: string
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <button className={`row-btn${on ? ' on' : ''}`} onClick={onToggle} aria-pressed={on}>
        {on ? 'On' : 'Off'}
      </button>
      {hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}
